/**
 * Define ServiceNow table-feed and credential API contracts.
 *
 * One feed owns one allowlisted table and bounded current record window. OAuth
 * fields stay provider-specific because ServiceNow needs both an application
 * credential and an integration-user credential rather than Kuali's API key.
 */
import type {
  ConnectorCredentialSource,
  ConnectorSourceDisplayFieldDefinition,
} from '../types'

export type ServiceNowTable =
  | 'sys_user'
  | 'sys_user_group'
  | 'incident'
  | 'kb_knowledge'
  | 'sc_request'
  | 'sc_req_item'

export const serviceNowTableLabels: Record<ServiceNowTable, string> = {
  sys_user: 'Users',
  sys_user_group: 'Groups',
  incident: 'Incidents',
  kb_knowledge: 'Knowledge articles',
  sc_request: 'Catalog requests',
  sc_req_item: 'Requested items',
}

export const serviceNowSourceNames: Record<ServiceNowTable, string> = {
  sys_user: 'ServiceNow users',
  sys_user_group: 'ServiceNow groups',
  incident: 'ServiceNow incidents',
  kb_knowledge: 'ServiceNow knowledge articles',
  sc_request: 'ServiceNow catalog requests',
  sc_req_item: 'ServiceNow requested items',
}

export interface ServiceNowFeedOptions {
  apiUrl: string
  credentialSource: ConnectorCredentialSource
  recordLimit: number
  filters: ServiceNowFilter[]
}

export type ServiceNowFilterOperator =
  | 'is'
  | 'is-not'
  | 'contains'
  | 'starts-with'
  | 'on-or-after'
  | 'on-or-before'

export interface ServiceNowFilter {
  field: string
  operator: ServiceNowFilterOperator
  value: string
}

export interface ServiceNowFilterChoice {
  value: string
  label: string
}

export interface ServiceNowFilterField {
  name: string
  label: string
  valueType: 'text' | 'boolean' | 'choice' | 'date'
  operators: ServiceNowFilterOperator[]
  choices: ServiceNowFilterChoice[]
}

export interface ServiceNowTableDefinition {
  table: ServiceNowTable
  label: string
  module: string
  filterFields: ServiceNowFilterField[]
  defaultFilters: ServiceNowFilter[]
  sourceDisplayFields: ConnectorSourceDisplayFieldDefinition[]
}

export interface ServiceNowPreviewColumn {
  field: string
  label: string
  filterField?: string
}

export type ServiceNowTableAccess = 'readable' | 'empty' | 'denied' | 'unknown'

export interface ServiceNowTableCandidate {
  table: string
  label: string
  module: string
  access: ServiceNowTableAccess
}

export interface ServiceNowInstanceTable {
  table: string
  label: string
}

export interface ServiceNowTableDefinitions {
  supportedTables: ServiceNowTableDefinition[]
}

export interface ServiceNowTableAccessCatalog {
  commonTables: ServiceNowTableCandidate[]
  instanceDiscoveryAvailable: boolean
  instanceTables: ServiceNowInstanceTable[]
}

export interface ServiceNowSelectionPreviewRow {
  sysId: string
  values: Record<string, string>
  filterValues: Record<string, string>
}

export interface ServiceNowSelectionPreview {
  windowCount: number
  matchCount: number
  columns: ServiceNowPreviewColumn[]
  rows: ServiceNowSelectionPreviewRow[]
}

export interface ServiceNowConnectionStatus {
  workspaceConfigured: boolean | null
  umdConfigured: boolean | null
  umdCredentialKeys: string[]
}

export interface ServiceNowWorkspaceCredentials {
  clientId: string
  clientSecret: string
  username: string
  password: string
}
